import {
  ChatCircle,
  ChatsCircle,
  Crown,
  Fire,
  Flame,
  GitCommit,
  Kanban,
  Lock,
  Mountains,
  RocketLaunch,
  Rows,
  Ticket,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/** Visual config keyed by badge id from the API. */
const BADGE_VISUAL = {
  first_commit: {
    Icon: GitCommit,
    ring: 'from-sky-400 to-blue-600',
    glow: 'shadow-sky-200',
    iconBg: 'bg-gradient-to-br from-sky-400 to-blue-600',
  },
  commit_10: {
    Icon: GitCommit,
    ring: 'from-cyan-400 to-teal-600',
    glow: 'shadow-cyan-200',
    iconBg: 'bg-gradient-to-br from-cyan-400 to-teal-600',
  },
  commit_50: {
    Icon: RocketLaunch,
    ring: 'from-indigo-400 to-violet-600',
    glow: 'shadow-indigo-200',
    iconBg: 'bg-gradient-to-br from-indigo-400 to-violet-600',
  },
  lines_1k: {
    Icon: Rows,
    ring: 'from-violet-400 to-purple-600',
    glow: 'shadow-violet-200',
    iconBg: 'bg-gradient-to-br from-violet-400 to-purple-600',
  },
  lines_10k: {
    Icon: Mountains,
    ring: 'from-fuchsia-400 to-purple-700',
    glow: 'shadow-fuchsia-200',
    iconBg: 'bg-gradient-to-br from-fuchsia-500 to-purple-700',
  },
  comment_5: {
    Icon: ChatCircle,
    ring: 'from-emerald-400 to-green-600',
    glow: 'shadow-emerald-200',
    iconBg: 'bg-gradient-to-br from-emerald-400 to-green-600',
  },
  comment_25: {
    Icon: ChatsCircle,
    ring: 'from-lime-400 to-emerald-600',
    glow: 'shadow-lime-200',
    iconBg: 'bg-gradient-to-br from-lime-500 to-emerald-600',
  },
  ticket_5: {
    Icon: Ticket,
    ring: 'from-amber-400 to-orange-500',
    glow: 'shadow-amber-200',
    iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
  },
  ticket_25: {
    Icon: Kanban,
    ring: 'from-orange-400 to-red-500',
    glow: 'shadow-orange-200',
    iconBg: 'bg-gradient-to-br from-orange-400 to-red-500',
  },
  streak_3: {
    Icon: Fire,
    ring: 'from-orange-400 to-red-500',
    glow: 'shadow-orange-200',
    iconBg: 'bg-gradient-to-br from-orange-400 to-red-500',
  },
  streak_7: {
    Icon: Flame,
    ring: 'from-red-400 to-rose-600',
    glow: 'shadow-red-200',
    iconBg: 'bg-gradient-to-br from-red-500 to-rose-600',
  },
  streak_30: {
    Icon: Crown,
    ring: 'from-yellow-300 via-amber-400 to-orange-500',
    glow: 'shadow-amber-300',
    iconBg: 'bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500',
  },
};

const FALLBACK_VISUAL = {
  Icon: GitCommit,
  ring: 'from-zinc-300 to-zinc-500',
  glow: 'shadow-zinc-200',
  iconBg: 'bg-gradient-to-br from-zinc-400 to-zinc-600',
};

function BadgeTile({ badge, lockedLabel }) {
  const visual = BADGE_VISUAL[badge.id] || FALLBACK_VISUAL;
  const { Icon } = visual;
  const earned = badge.earned;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`group relative flex flex-col items-center text-center p-2 rounded-md transition-transform duration-200 ${
            earned ? 'hover:-translate-y-0.5 cursor-default' : 'opacity-80'
          }`}
          data-testid={`badge-${badge.id}`}
          data-earned={earned ? 'true' : 'false'}
        >
          <div className="relative mb-2">
            {earned && (
              <span
                className={`absolute inset-0 rounded-full blur-md opacity-40 ${visual.iconBg}`}
                aria-hidden
              />
            )}
            <div
              className={`relative w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                earned
                  ? `${visual.iconBg} border-white/30 shadow-lg ${visual.glow} group-hover:scale-105`
                  : 'bg-zinc-100 border-zinc-200 border-dashed'
              }`}
            >
              <Icon
                size={26}
                weight={earned ? 'fill' : 'regular'}
                className={earned ? 'text-white drop-shadow-sm' : 'text-zinc-400'}
              />
              {!earned && (
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-zinc-200 border border-white flex items-center justify-center">
                  <Lock size={10} weight="bold" className="text-zinc-500" />
                </span>
              )}
            </div>
          </div>
          <span
            className={`text-[10px] leading-tight font-semibold max-w-[5.5rem] ${
              earned ? 'text-zinc-800' : 'text-zinc-400'
            }`}
          >
            {badge.title}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center bg-zinc-900 text-white border-0">
        <p className="font-semibold mb-0.5">{badge.title}</p>
        <p className="text-zinc-300 font-normal">{badge.description}</p>
        {!earned && (
          <p className="text-zinc-500 mt-1 text-[10px] uppercase tracking-wider">{lockedLabel}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default function BadgeGrid({ badges = [], lockedLabel = 'Locked' }) {
  if (!badges.length) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1 sm:gap-2"
        data-testid="badge-grid"
      >
        {badges.map((badge) => (
          <BadgeTile key={badge.id} badge={badge} lockedLabel={lockedLabel} />
        ))}
      </div>
    </TooltipProvider>
  );
}
